import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { useLazyLoad } from "../hooks/useLazyLoad";
import { AdminProject } from "../types/admin";
import { projectsApi } from "../services/api";
import ImageCarousel from "./ImageCarousel";
import "../css/projects.css";
import "../css/carousel.css";

const openUrl = (url: string) => {
  if (!url) return;
  const fullUrl = url.startsWith("http") ? url : `https://${url}`;
  window.open(fullUrl, "_blank", "noopener,noreferrer");
};

const Projects: React.FC = () => {
  const { lang, t } = useLanguage();
  const [sectionRef, isVisible] = useLazyLoad("300px");
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [detailProject, setDetailProject] = useState<AdminProject | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [visibleRows, setVisibleRows] = useState(2);
  const gridRef = useRef<HTMLDivElement>(null);

  const COLS_DESKTOP = 3;
  const COLS_TABLET = 2;
  const COLS_MOBILE = 1;
  const ROWS_STEP = 2;

  const getColumns = useCallback(() => {
    const w = window.innerWidth;
    if (w <= 480) return COLS_MOBILE;
    if (w <= 1200) return COLS_TABLET;
    return COLS_DESKTOP;
  }, []);

  const visibleCount = visibleRows * getColumns();
  const totalRows = Math.ceil(projects.length / getColumns());
  const hasMore = visibleRows < totalRows;

  useEffect(() => {
    if (!isVisible) return;
    const loadProjects = async () => {
      try {
        const response = await projectsApi.getAll({ pageSize: 100 });
        if (response.success && response.data) {
          const projectsWithImages = await Promise.all(
            response.data.map(async (project) => {
              try {
                const imgRes = await projectsApi.getImages(project.id);
                if (imgRes.success && imgRes.data && imgRes.data.length > 0) {
                  return {
                    ...project,
                    images: imgRes.data.map((url, i) => ({
                      id: project.images[i]?.id ?? i,
                      url,
                    })),
                  };
                }
              } catch {
                // keep original images
              }
              return project;
            })
          );
          setProjects(projectsWithImages);
        }
      } catch {
        // silently fail
      } finally {
        setLoaded(true);
      }
    };

    void loadProjects();
  }, [isVisible]);

  const openDetail = async (id: string | number) => {
    setDetailLoading(true);
    setDetailProject(null);
    try {
      const response = await projectsApi.getById(id);
      if (response.success && response.data) {
        // resolve image URLs
        let project = response.data;
        try {
          const imgRes = await projectsApi.getImages(id);
          if (imgRes.success && imgRes.data && imgRes.data.length > 0) {
            project = {
              ...project,
              images: imgRes.data.map((url, i) => ({
                id: project.images[i]?.id ?? i,
                url,
              })),
            };
          }
        } catch { /* keep original */ }
        setDetailProject(project);
      }
    } catch { /* silently fail */ }
    finally { setDetailLoading(false); }
  };

  const closeDetail = () => setDetailProject(null);

  if (!isVisible) {
    return (
      <section
        className="projects lazy-section"
        id="projects"
        ref={sectionRef as React.RefObject<HTMLElement>}
      />
    );
  }

  if (!loaded || projects.length === 0) {
    return (
      <section
        className="projects lazy-section"
        id="projects"
        ref={sectionRef as React.RefObject<HTMLElement>}
      />
    );
  }

  return (
    <section
      className={`projects lazy-section${loaded ? " lazy-section--visible" : ""}`}
      id="projects"
      ref={sectionRef as React.RefObject<HTMLElement>}
    >
      <div className="container">
        <h2 className="section-title">{t.projectsTitle}</h2>
        <p className="section-subtitle">{t.projectsSubtitle}</p>

        <div className="projects-grid" ref={gridRef}>
          {projects.slice(0, visibleCount).map((project) => {
            const description =
              lang === "pt" ? project.descriptionPt : project.descriptionEn;
            const imageUrls = project.images.map((img) => img.url);

            return (
              <div key={project.id} className="project-card">
                <div className="project-card-image-wrapper">
                  {imageUrls.length > 1 ? (
                    <ImageCarousel images={imageUrls} alt={project.name} />
                  ) : imageUrls.length === 1 ? (
                    <img
                      src={imageUrls[0]}
                      alt={project.name}
                      className="project-card-image"
                      loading="lazy"
                    />
                  ) : null}
                </div>
                <div className="project-card-body">
                  <div className="project-card-header">
                    <h3 className="project-card-title">{project.name}</h3>
                    <button
                      className="project-btn-details-inline"
                      onClick={() => openDetail(project.id)}
                      title={t.detailsBtn}
                    >
                      <i className="fa-solid fa-circle-info" />
                      <span>{t.detailsBtn}</span>
                    </button>
                  </div>
                  <p className="project-card-description">{description}</p>
                  <div className="project-card-tech">
                    {project.techStack.map((tech) => (
                      <span key={tech} className="tech-badge">
                        {tech}
                      </span>
                    ))}
                  </div>
                  <div className="project-card-buttons">
                    {project.viewUrl && (
                      <button
                        className="project-btn project-btn-view"
                        onClick={() => openUrl(project.viewUrl)}
                      >
                        <i className="fa-solid fa-eye" />
                        <span>{t.viewBtn}</span>
                      </button>
                    )}
                    {project.codeUrl && (
                      <button
                        className="project-btn project-btn-code"
                        onClick={() => openUrl(project.codeUrl)}
                      >
                        <i className="fa-brands fa-github" />
                        <span>{t.codeBtn}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {totalRows > 2 && (
          <div className="show-more-wrapper">
            <button
              className={`show-more-btn${!hasMore ? " expanded" : ""}`}
              onClick={() => {
                if (hasMore) {
                  setVisibleRows((prev) => prev + ROWS_STEP);
                } else {
                  setVisibleRows(2);
                  document.getElementById("projects")?.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              <span>
                {hasMore
                  ? lang === "pt"
                    ? "Ver Mais"
                    : "Show More"
                  : lang === "pt"
                  ? "Ver Menos"
                  : "Show Less"}
              </span>
              <i className="fa-solid fa-chevron-down" />
            </button>
          </div>
        )}

        {/* Detail Modal */}
        {(detailProject || detailLoading) && (
          <div className="project-detail-overlay" onClick={closeDetail}>
            <div className="project-detail-modal" onClick={(e) => e.stopPropagation()}>
              {detailLoading ? (
                <div className="project-detail-loading">
                  <div className="loading-spinner" />
                </div>
              ) : detailProject ? (
                <>
                  <button className="project-detail-close" onClick={closeDetail}>
                    <i className="fa-solid fa-xmark" />
                  </button>

                  <h2 className="project-detail-title">{detailProject.name}</h2>

                  <p className="project-detail-description">
                    {lang === "pt" ? detailProject.descriptionPt : detailProject.descriptionEn}
                  </p>

                  {detailProject.techStack.length > 0 && (
                    <div className="project-detail-tech">
                      {detailProject.techStack.map((tech) => (
                        <span key={tech} className="tech-badge">{tech}</span>
                      ))}
                    </div>
                  )}

                  {detailProject.images.length > 0 && (
                    <div className="project-detail-images">
                      {detailProject.images.map((img, i) => (
                        <div
                          key={`detail-${img.id}`}
                          className="project-detail-image-link"
                          onClick={() => setLightboxUrl(img.url)}
                          style={{ cursor: "pointer" }}
                        >
                          <img src={img.url} alt={`${detailProject.name} ${i + 1}`} />
                          <div className="project-detail-image-expand">
                            <i className="fa-solid fa-expand" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="project-detail-links">
                    {detailProject.viewUrl && (
                      <button
                        className="project-btn project-btn-view"
                        onClick={() => openUrl(detailProject.viewUrl)}
                      >
                        <i className="fa-solid fa-eye" />
                        <span>{t.viewBtn}</span>
                      </button>
                    )}
                    {detailProject.codeUrl && (
                      <button
                        className="project-btn project-btn-code"
                        onClick={() => openUrl(detailProject.codeUrl)}
                      >
                        <i className="fa-brands fa-github" />
                        <span>{t.codeBtn}</span>
                      </button>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        )}
        {/* Lightbox */}
        {lightboxUrl && (
          <div
            className="project-lightbox-overlay"
            onClick={() => setLightboxUrl(null)}
          >
            <button
              className="project-lightbox-close"
              onClick={() => setLightboxUrl(null)}
            >
              <i className="fa-solid fa-xmark" />
            </button>
            <img
              src={lightboxUrl}
              alt="Fullscreen"
              className="project-lightbox-img"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
